/**
 * Phase 8 · Cluster C-41 · Assistant thread hook (real runner).
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.5
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.5
 *
 * Single state owner for every Assistant surface:
 *   - Smart Match agent column (`scope="smart-match"`).
 *   - Topbar slide-over (`scope="page"`).
 *   - Record detail Assistant tab (`scope="record"`).
 *
 * Compared to the prior placeholder (a local echo returning a canned
 * reply), this hook drives a real streaming runner through
 * `agentRunnerService.streamReply` — tokens, tool calls, tool results
 * and reasoning steps all land through one event loop. It also
 * persists every turn to `ggj_b2b_agent_threads` / `ggj_b2b_agent_turns`
 * (migration 106) so a refresh replays from the server, not localStorage.
 *
 * Callers that already own a bespoke pipeline (Smart Match's
 * `AgentThreadColumn` — which fuses `invokeLlamaChat` with the legacy
 * `agentTools` registry) can still pass a custom `runPrompt` to get a
 * one-shot result. We persist user + assistant turns in both paths.
 *
 * The local cache remains for UI replay while the server call is
 * in-flight, but server-side turns win on conflict during the mount
 * hydration step.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@core/auth/useAuth';
import {
  agentRunnerService,
  type AgentEvent,
  type AgentRunnerContext,
  type AgentStepStatus,
  type PlanStep,
} from '@core/supabase/services/agentRunnerService';
import type {
  AgentContextBlock,
  AgentRouteContext,
} from '../context/types';
import { formatAgentContextBlock } from './useAgentRouteContext';

export type AgentScope = 'smart-match' | 'page' | 'record';

export interface AgentReasoningStep {
  name: string;
  /** Duration in ms. */
  durationMs: number;
}

export interface AgentActionChip {
  id: string;
  label: string;
  onClick?: () => void;
}

/** Inline "Using <tool>…" pill rendered beneath a streaming turn. */
export interface AgentToolCall {
  id: string;
  name: string;
  argsJson?: string;
  /** Flips to true when a matching tool_result arrives. */
  done?: boolean;
}

/**
 * Phase 8 · C-48 — UI-side plan state attached to the streaming
 * assistant turn when the runner emits a `plan` event. The card
 * component (`AgentPlanCard`) reads this off the turn and calls back
 * into the hook (`approvePlan` / `rejectPlan` / `cancelPlan`) to drive
 * the approval state machine.
 */
export interface AgentTurnPlan {
  planId: string;
  steps: PlanStep[];
  /**
   * Observed state across both the persisted `approval_state` and the
   * per-step execution status. We don't round-trip the DB on every
   * event — the runner's `step_status` / `done` / `error` events drive
   * these locally and `executing` flips once the first step starts.
   */
  state:
    | 'pending'
    | 'approved'
    | 'executing'
    | 'done'
    | 'failed'
    | 'rejected'
    | 'cancelled';
  /** Per-step status keyed by index — drives the AgentStepStatus strip. */
  stepStatuses: Record<number, { status: AgentStepStatus; error?: string }>;
  /** Populated when the runner emits a `step_status: 'failed'` event. */
  failureReason?: string;
}

/** Muted card rendered under an assistant turn once a tool returned. */
export interface AgentToolResult {
  id: string;
  name: string;
  summary: string;
  ok: boolean;
  /**
   * Raw tool result payload — present only on live (streaming) turns.
   * Used by the UI to render richer, interactive cards (e.g. the
   * SourcingResult rows with a "Save to pool" CTA). Server-replayed
   * turns leave this undefined because we only persist the compact
   * summary to `ggj_b2b_agent_turns`.
   */
  data?: unknown;
}

export interface AgentTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  ts: number;
  /** Streaming assistant turn — render `• • •` placeholder + filling border. */
  streaming?: boolean;
  /** Per-turn reasoning trace (collapsed by default). */
  reasoning?: {
    thoughtMs: number;
    steps: AgentReasoningStep[];
  };
  /** Action chips rendered below an assistant turn. */
  chips?: AgentActionChip[];
  /** Inline tool-call pills. */
  toolCalls?: AgentToolCall[];
  /** Tool-result cards rendered after the streaming content. */
  toolResults?: AgentToolResult[];
  /**
   * Phase 8 · C-48 — present when the runner proposed a plan for this
   * turn. The turn stays on-screen in `state='pending'` until the user
   * clicks Approve/Reject on the Plan card; subsequent
   * `executePlan` events patch the same turn so there's only ever one
   * plan card per turn.
   */
  plan?: AgentTurnPlan;
}

export interface AgentThreadKey {
  scope: AgentScope;
  scopeId: string;
  entityKind?: string;
  entityId?: string;
}

interface PersistedThread {
  turns: AgentTurn[];
  updatedAt: number;
}

interface RunPromptResult {
  /** The assistant content text (full response). */
  content: string;
  reasoning?: { thoughtMs: number; steps: AgentReasoningStep[] };
  chips?: AgentActionChip[];
  toolResults?: AgentToolResult[];
}

export interface UseAgentThreadOptions extends AgentThreadKey {
  /**
   * Legacy one-shot runner override. When provided, the hook does NOT use
   * the streaming `agentRunnerService.streamReply` path — instead it awaits
   * this function and renders the final payload. Smart Match's
   * `AgentThreadColumn` uses this to keep its tool-aware pipeline.
   */
  runPrompt?: (userText: string) => Promise<RunPromptResult>;
  /** Default chips to show on the empty state. */
  emptyChips?: AgentActionChip[];
  /**
   * Whitelist of tool names the runner is allowed to call. When omitted
   * the runner uses its own defaults (Smart Match passes all tools; the
   * record-detail rail passes `['data.candidates.list','data.jobs.list']`).
   */
  tools?: string[];
  /**
   * Phase 8 · C-48 — Route / entity context injected as a system-level
   * block at the top of every user turn's outgoing message. Lets the LLM
   * scope its suggestions to "the user is on /requisitions/REQ-…" without
   * requiring every caller to stuff the text into their own prompt.
   *
   * Additive: existing callers (Smart Match, record-detail rails that
   * haven't wired the hook yet) pass undefined and behaviour is
   * unchanged. The context block is only emitted when `routeContext` is
   * provided.
   */
  routeContext?: AgentRouteContext | null;
  /**
   * Phase 8 · C-48 — Autonomous mode flag. Forwarded to the runner so a
   * single reversible write call executes inline (audit row tagged
   * `approval_mode='auto'`) when the tool is NOT on the safety list.
   * Defaults to false for back-compat; the toggle component
   * (`AgentAutonomousToggle`) persists and passes the user's
   * preference.
   */
  autonomousMode?: boolean;
  /**
   * Callback fired when sourcing tool results arrive (source.* tools).
   * Used by the Smart Match canvas to surface external sourcing results
   * as match cards in the grid.
   */
  onSourcingResults?: (results: AgentToolResult[]) => void;
}

function storageKey(k: AgentThreadKey): string {
  return `ggj.assistant.threads.${k.scope}.${k.scopeId || '_'}`;
}

function loadThread(k: AgentThreadKey): PersistedThread | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(k));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedThread;
    if (!parsed || !Array.isArray(parsed.turns)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveThread(k: AgentThreadKey, turns: AgentTurn[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      storageKey(k),
      JSON.stringify({ turns, updatedAt: Date.now() } satisfies PersistedThread),
    );
  } catch {
    // Ignore quota errors — the server is the source of truth now (C-41).
  }
}

export interface UseAgentThreadReturn {
  turns: AgentTurn[];
  thinking: boolean;
  /** Send a user prompt and stream an assistant turn. */
  send: (text: string) => Promise<void>;
  /** Clear this surface's history (UI + server). */
  reset: () => void;
  /**
   * Phase 8 · C-48 — resume execution of a pending plan. `mode='all'`
   * runs every step; `mode='stepwise'` runs one step and returns. The
   * hook patches the owning turn as events stream back.
   */
  approvePlan: (planId: string, mode: 'all' | 'stepwise') => Promise<void>;
  /** Reject a pending plan — flips the card to a terminal rejected state. */
  rejectPlan: (planId: string) => Promise<void>;
  /** Cancel a pending plan the user no longer wants to see. */
  cancelPlan: (planId: string) => Promise<void>;
}

export function useAgentThread(opts: UseAgentThreadOptions): UseAgentThreadReturn {
  const {
    scope,
    scopeId,
    entityKind,
    entityId,
    runPrompt,
    emptyChips: _emptyChips,
    tools,
    routeContext,
    autonomousMode = false,
    onSourcingResults,
  } = opts;

  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const userId = user?.orgUserId ?? '';

  const key = useMemo<AgentThreadKey>(
    () => ({ scope, scopeId, entityKind, entityId }),
    [scope, scopeId, entityKind, entityId],
  );

  const [turns, setTurns] = useState<AgentTurn[]>(() => loadThread(key)?.turns ?? []);
  const [thinking, setThinking] = useState(false);
  const abortRef = useRef(false);
  const threadIdRef = useRef<string | null>(null);

  const ctx: AgentRunnerContext = useMemo(
    () => ({ orgId, userId, scope, scopeId, entityKind, entityId }),
    [orgId, userId, scope, scopeId, entityKind, entityId],
  );

  // Reload turns when surface scope changes — start from the local cache
  // immediately so the surface is interactive, then let the server sync
  // replace it if it returns anything (server wins).
  useEffect(() => {
    setTurns(loadThread(key)?.turns ?? []);
    threadIdRef.current = null;
  }, [key]);

  // Hydrate from the server. Guarded on auth because `ensureThread`
  // requires `orgId` to scope the lookup.
  useEffect(() => {
    if (!orgId || !scopeId) return;
    let cancelled = false;
    void (async () => {
      const id = await agentRunnerService.ensureThread(ctx);
      if (cancelled || !id) return;
      threadIdRef.current = id;
      const serverTurns = await agentRunnerService.listTurns(id);
      if (cancelled || serverTurns.length === 0) return;
      // Server wins: convert persisted turns into the UI shape.
      setTurns(
        serverTurns.map((t) => {
          const chips = (t.chips ?? undefined) as AgentActionChip[] | undefined;
          const toolResults = (t.toolResults ?? undefined) as AgentToolResult[] | undefined;
          return {
            id: t.id,
            role: t.role === 'system' ? 'assistant' : t.role,
            content: t.content,
            ts: new Date(t.ts).getTime(),
            reasoning: t.reasoning ?? undefined,
            chips,
            toolResults,
          } satisfies AgentTurn;
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx, orgId, scopeId]);

  // Persist to local cache on every change (kept as warm cache while the
  // server round-trips — prevents a flash of empty state on reload).
  useEffect(() => {
    saveThread(key, turns);
  }, [key, turns]);

  const patchTurn = useCallback((id: string, updater: (t: AgentTurn) => AgentTurn) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? updater(t) : t)));
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || thinking) return;

      // Phase 8 · C-48 — Inject route/entity context as a system-level
      // prefix on the outgoing user text. Logged to console.debug in dev
      // so we can verify in devtools that the LLM sees the context.
      let outboundText = trimmed;
      if (routeContext) {
        const block: AgentContextBlock = {
          route: routeContext.route,
          scopeKind: routeContext.scopeKind,
          entity: routeContext.entity,
          recentEntities: routeContext.recentEntities,
        };
        const ctxText = formatAgentContextBlock({
          ...block,
          availableActions: routeContext.availableActions,
        });
        outboundText = `${ctxText}\n\n---\n\n${trimmed}`;
        if (import.meta.env.DEV) {
          // Devtools-visible single log per turn (acceptance §10.2.8).
          console.debug('[agent] context', {
            route: routeContext.route,
            scopeKind: routeContext.scopeKind,
            entity: routeContext.entity ?? null,
            recentCount: routeContext.recentEntities.length,
          });
        }
      }

      const userTurn: AgentTurn = {
        id: crypto.randomUUID(),
        role: 'user',
        content: trimmed,
        ts: Date.now(),
      };
      const streamingId = crypto.randomUUID();
      const streamingTurn: AgentTurn = {
        id: streamingId,
        role: 'assistant',
        content: '',
        ts: Date.now(),
        streaming: true,
      };
      setTurns((prev) => [...prev, userTurn, streamingTurn]);
      setThinking(true);
      abortRef.current = false;

      // Persist the user turn immediately so the server sees it even if
      // the runner blows up before the assistant turn completes.
      let threadId = threadIdRef.current;
      if (!threadId && orgId && scopeId) {
        threadId = await agentRunnerService.ensureThread(ctx);
        threadIdRef.current = threadId;
      }
      if (threadId) {
        void agentRunnerService.saveTurn(threadId, { role: 'user', content: trimmed });
      }

      try {
        if (runPrompt) {
          // Legacy one-shot override (Smart Match canvas).
          const result = await runPrompt(outboundText);
          if (abortRef.current) return;
          patchTurn(streamingId, (t) => ({
            ...t,
            content: result.content,
            reasoning: result.reasoning,
            chips: result.chips,
            toolResults: result.toolResults,
            streaming: false,
            ts: Date.now(),
          }));
          if (threadId) {
            void agentRunnerService.saveTurn(threadId, {
              role: 'assistant',
              content: result.content,
              reasoning: result.reasoning ?? null,
              chips: result.chips?.map((c) => ({ id: c.id, label: c.label })) ?? null,
              toolResults: result.toolResults ?? null,
            });
          }
          return;
        }

        // Streaming runner.
        const iter = agentRunnerService.streamReply(ctx, outboundText, {
          toolNames: tools,
          // C-42: pass the resolved thread UUID so tool invocations audit
          // against the right thread row. Falsy values are fine — the
          // service skips the audit write when threadId is absent.
          threadId,
          // C-48: forward the user's autonomous-mode preference so a
          // single reversible write executes inline. The runner still
          // gates multi-step plans on the Plan card regardless.
          autonomousMode,
        });
        const steps: AgentReasoningStep[] = [];
        const toolResultsAcc: AgentToolResult[] = [];
        const chipsAcc: AgentActionChip[] = [];
        let acc = '';
        let thoughtMs = 0;
        let failed = false;

        for await (const ev of iter as AsyncIterable<AgentEvent>) {
          if (abortRef.current) return;
          switch (ev.kind) {
            case 'token': {
              acc += ev.text;
              patchTurn(streamingId, (t) => ({ ...t, content: acc }));
              break;
            }
            case 'tool_call': {
              const call: AgentToolCall = {
                id: crypto.randomUUID(),
                name: ev.name,
                argsJson: ev.argsJson,
                done: false,
              };
              patchTurn(streamingId, (t) => ({
                ...t,
                toolCalls: [...(t.toolCalls ?? []), call],
              }));
              break;
            }
            case 'tool_result': {
              const result: AgentToolResult = {
                id: crypto.randomUUID(),
                name: ev.name,
                summary: ev.summary,
                ok: ev.ok,
                data: ev.data,
              };
              toolResultsAcc.push(result);
              patchTurn(streamingId, (t) => ({
                ...t,
                toolResults: [...(t.toolResults ?? []), result],
                toolCalls: (t.toolCalls ?? []).map((c) =>
                  c.name === ev.name && !c.done ? { ...c, done: true } : c,
                ),
              }));
              if (ev.name.startsWith('source.') && ev.ok && onSourcingResults) {
                onSourcingResults([result]);
              }
              break;
            }
            case 'chip': {
              const chip: AgentActionChip = { id: ev.id, label: ev.label };
              chipsAcc.push(chip);
              patchTurn(streamingId, (t) => ({
                ...t,
                chips: [...(t.chips ?? []), chip],
              }));
              break;
            }
            case 'reasoning_step': {
              steps.push({ name: ev.name, durationMs: ev.durationMs });
              patchTurn(streamingId, (t) => ({
                ...t,
                reasoning: { thoughtMs: t.reasoning?.thoughtMs ?? 0, steps: [...steps] },
              }));
              break;
            }
            case 'plan': {
              // Runner has persisted a pending plan and halted the stream.
              // We attach the plan state to the turn and stop thinking —
              // the user's Approve click on the Plan card drives
              // `approvePlan` below, which re-enters the stream loop.
              patchTurn(streamingId, (t) => ({
                ...t,
                plan: {
                  planId: ev.planId,
                  steps: ev.steps,
                  state: 'pending',
                  stepStatuses: {},
                },
              }));
              break;
            }
            case 'step_status': {
              // Individual step pill update during `executePlan`. The
              // map is keyed by index so we can ignore out-of-order
              // updates safely (last writer wins per step).
              patchTurn(streamingId, (t) => {
                if (!t.plan || t.plan.planId !== ev.planId) return t;
                const nextStatuses = {
                  ...t.plan.stepStatuses,
                  [ev.stepIndex]: { status: ev.status, error: ev.error },
                };
                const nextState =
                  ev.status === 'failed'
                    ? 'failed'
                    : t.plan.state === 'pending' || t.plan.state === 'approved'
                      ? 'executing'
                      : t.plan.state;
                return {
                  ...t,
                  plan: {
                    ...t.plan,
                    stepStatuses: nextStatuses,
                    state: nextState,
                    failureReason:
                      ev.status === 'failed' ? ev.error ?? 'step failed' : t.plan.failureReason,
                  },
                };
              });
              break;
            }
            case 'done': {
              thoughtMs = ev.thoughtMs;
              break;
            }
            case 'error': {
              failed = true;
              patchTurn(streamingId, (t) => ({
                ...t,
                content:
                  acc || `The assistant is not available right now: ${ev.message}`,
                streaming: false,
                ts: Date.now(),
              }));
              break;
            }
          }
        }

        if (abortRef.current) return;
        if (!failed) {
          patchTurn(streamingId, (t) => ({
            ...t,
            streaming: false,
            ts: Date.now(),
            reasoning: steps.length ? { thoughtMs, steps } : t.reasoning,
          }));
        }

        if (threadId) {
          void agentRunnerService.saveTurn(threadId, {
            role: 'assistant',
            content: acc || (failed ? '[error]' : ''),
            reasoning: steps.length ? { thoughtMs, steps } : null,
            chips: chipsAcc.length ? chipsAcc.map((c) => ({ id: c.id, label: c.label })) : null,
            toolResults: toolResultsAcc.length ? toolResultsAcc : null,
          });
        }
      } catch (err) {
        if (abortRef.current) return;
        const msg = err instanceof Error ? err.message : 'unknown error';
        patchTurn(streamingId, (t) => ({
          ...t,
          content: `I hit an issue: ${msg}. Try again shortly.`,
          streaming: false,
          ts: Date.now(),
        }));
      } finally {
        setThinking(false);
      }
    },
    [ctx, orgId, scopeId, patchTurn, runPrompt, thinking, tools, routeContext, autonomousMode],
  );

  /**
   * Phase 8 · C-48 — resume a pending plan. Mirrors `send`'s streaming
   * loop but consumes `agentRunnerService.executePlan` instead. Patches
   * the owning turn (the one whose `plan.planId` matches) so step
   * pills, tool-call bubbles and tool-result cards land on the same
   * bubble the Plan card was attached to.
   */
  const approvePlan = useCallback(
    async (planId: string, mode: 'all' | 'stepwise') => {
      if (!planId || thinking) return;

      // Locate the turn that owns this plan so subsequent events patch
      // the right bubble. If the user refreshes between plan creation
      // and approval, the turn may not exist yet — we silently bail in
      // that case (Plan card UI will refetch).
      const owningTurnId = (() => {
        let match: string | null = null;
        setTurns((prev) => {
          const t = prev.find((row) => row.plan?.planId === planId);
          if (t) match = t.id;
          return prev;
        });
        return match;
      })();

      if (!owningTurnId) {
        console.warn('[useAgentThread.approvePlan] no owning turn for', planId);
        return;
      }

      // Flip the plan state visually before the first event arrives so
      // the Plan card shows a disabled/busy state immediately.
      patchTurn(owningTurnId, (t) =>
        t.plan && t.plan.planId === planId
          ? {
              ...t,
              plan: { ...t.plan, state: 'approved' },
              streaming: true,
            }
          : t,
      );
      setThinking(true);

      try {
        const iter = agentRunnerService.executePlan(planId, mode, ctx);
        const extraSteps: AgentReasoningStep[] = [];
        const extraToolResults: AgentToolResult[] = [];

        for await (const ev of iter as AsyncIterable<AgentEvent>) {
          if (abortRef.current) return;
          switch (ev.kind) {
            case 'tool_call': {
              const call: AgentToolCall = {
                id: crypto.randomUUID(),
                name: ev.name,
                argsJson: ev.argsJson,
                done: false,
              };
              patchTurn(owningTurnId, (t) => ({
                ...t,
                toolCalls: [...(t.toolCalls ?? []), call],
              }));
              break;
            }
            case 'tool_result': {
              const result: AgentToolResult = {
                id: crypto.randomUUID(),
                name: ev.name,
                summary: ev.summary,
                ok: ev.ok,
                data: ev.data,
              };
              extraToolResults.push(result);
              patchTurn(owningTurnId, (t) => ({
                ...t,
                toolResults: [...(t.toolResults ?? []), result],
                toolCalls: (t.toolCalls ?? []).map((c) =>
                  c.name === ev.name && !c.done ? { ...c, done: true } : c,
                ),
              }));
              if (ev.name.startsWith('source.') && ev.ok && onSourcingResults) {
                onSourcingResults([result]);
              }
              break;
            }
            case 'step_status': {
              patchTurn(owningTurnId, (t) => {
                if (!t.plan || t.plan.planId !== ev.planId) return t;
                const nextStatuses = {
                  ...t.plan.stepStatuses,
                  [ev.stepIndex]: { status: ev.status, error: ev.error },
                };
                const nextState =
                  ev.status === 'failed' ? 'failed' : 'executing';
                return {
                  ...t,
                  plan: {
                    ...t.plan,
                    stepStatuses: nextStatuses,
                    state: nextState,
                    failureReason:
                      ev.status === 'failed' ? ev.error ?? 'step failed' : t.plan.failureReason,
                  },
                };
              });
              break;
            }
            case 'reasoning_step': {
              extraSteps.push({ name: ev.name, durationMs: ev.durationMs });
              patchTurn(owningTurnId, (t) => ({
                ...t,
                reasoning: {
                  thoughtMs: t.reasoning?.thoughtMs ?? 0,
                  steps: [...(t.reasoning?.steps ?? []), ...extraSteps.slice(-1)],
                },
              }));
              break;
            }
            case 'done': {
              patchTurn(owningTurnId, (t) => ({
                ...t,
                streaming: false,
                ts: Date.now(),
                plan: t.plan
                  ? {
                      ...t.plan,
                      // If any step failed, state is already 'failed'.
                      state:
                        t.plan.state === 'failed'
                          ? 'failed'
                          : mode === 'all' ||
                              Object.keys(t.plan.stepStatuses).length >= t.plan.steps.length
                            ? 'done'
                            : 'executing',
                    }
                  : undefined,
              }));
              break;
            }
            case 'error': {
              patchTurn(owningTurnId, (t) => ({
                ...t,
                streaming: false,
                ts: Date.now(),
                plan: t.plan
                  ? { ...t.plan, state: 'failed', failureReason: ev.message }
                  : undefined,
              }));
              break;
            }
          }
        }

        if (threadIdRef.current && extraToolResults.length > 0) {
          // Persist post-approval tool results so a refresh replays
          // them. We append rather than overwrite because the user
          // turn already has a saved row from the pre-plan phase.
          void agentRunnerService.saveTurn(threadIdRef.current, {
            role: 'assistant',
            content: '[plan executed]',
            chips: null,
            toolResults: extraToolResults,
            reasoning: null,
          });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'plan failed';
        patchTurn(owningTurnId, (t) => ({
          ...t,
          streaming: false,
          plan: t.plan ? { ...t.plan, state: 'failed', failureReason: msg } : undefined,
        }));
      } finally {
        setThinking(false);
      }
    },
    [ctx, patchTurn, thinking],
  );

  const rejectPlan = useCallback(
    async (planId: string) => {
      if (!planId) return;
      // Optimistically flip state; let the server transition confirm.
      setTurns((prev) =>
        prev.map((t) =>
          t.plan?.planId === planId
            ? { ...t, plan: { ...t.plan, state: 'rejected' }, streaming: false }
            : t,
        ),
      );
      await agentRunnerService.plans.rejectPlan(planId, userId);
    },
    [userId],
  );

  const cancelPlan = useCallback(
    async (planId: string) => {
      if (!planId) return;
      setTurns((prev) =>
        prev.map((t) =>
          t.plan?.planId === planId
            ? { ...t, plan: { ...t.plan, state: 'cancelled' }, streaming: false }
            : t,
        ),
      );
      await agentRunnerService.cancelPlan(planId);
    },
    [],
  );

  const reset = useCallback(() => {
    abortRef.current = true;
    setTurns([]);
    setThinking(false);
    const id = threadIdRef.current;
    if (id) void agentRunnerService.resetThread(id);
  }, []);

  // External callers (Smart Match keyboard shortcuts, CandidateContextRail's
  // ask-input) dispatch `ggj:agent-thread:external-prompt` events so they
  // can inject a turn without holding a reference to `send` directly.
  useEffect(() => {
    const onExternal = (e: Event) => {
      const ce = e as CustomEvent<{ scopeId: string; text: string }>;
      if (!ce.detail) return;
      if (ce.detail.scopeId !== scopeId) return;
      void send(ce.detail.text);
    };
    window.addEventListener('ggj:agent-thread:external-prompt', onExternal);
    return () => window.removeEventListener('ggj:agent-thread:external-prompt', onExternal);
  }, [scopeId, send]);

  return { turns, thinking, send, reset, approvePlan, rejectPlan, cancelPlan };
}
